import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { clientService } from "../services/clientService";
import {
  ClientCaseDetail,
  ClientJourneyResponse,
  ClientJourneyStep,
} from "../types/clientPortal.types";

interface UseClientDashboardResult {
  clientCase: ClientCaseDetail | null;
  journey: ClientJourneyResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const toErrorMessage = (fallback: string, cause?: unknown): string => {
  if (typeof cause === "object" && cause !== null && "response" in cause) {
    const response = (cause as { response?: { data?: { detail?: string } } }).response;
    if (typeof response?.data?.detail === "string" && response.data.detail.trim()) {
      return response.data.detail;
    }
  }
  return fallback;
};

const normalizeStageToken = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
};

const normalizeStageLabel = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
};

const normalizeCaseStages = (clientCase: ClientCaseDetail): ClientJourneyStep[] => {
  const orderedStages = [...clientCase.workflow_template_stages].sort((a, b) => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.label.localeCompare(b.label);
  });

  if (orderedStages.length === 0) return [];

  const normalizedCurrentKey = normalizeStageToken(clientCase.lifecycle_status);
  const normalizedCurrentLabel = normalizeStageLabel(clientCase.current_stage_label);
  let activeIndex = orderedStages.findIndex((stage) => {
    const normalizedStageKey = normalizeStageToken(stage.key);
    if (normalizedStageKey && normalizedStageKey === normalizedCurrentKey) {
      return true;
    }
    // Some workflows store case stage labels/keys with different separators or aliases.
    const normalizedStageLabel = normalizeStageLabel(stage.label);
    if (normalizedCurrentLabel && normalizedStageLabel === normalizedCurrentLabel) {
      return true;
    }
    return (
      normalizedCurrentKey.length > 0 &&
      normalizedStageLabel.length > 0 &&
      normalizeStageLabel(normalizedCurrentKey) === normalizedStageLabel
    );
  });
  if (activeIndex < 0) {
    activeIndex = 0;
  }

  const isClosed = Boolean(clientCase.closed_at);
  const isCurrentDone = clientCase.stage_status.trim().toUpperCase() === "DONE";

  const steps = orderedStages.map((stage, index) => {
    let state: ClientJourneyStep["state"] = "pending";
    if (isClosed || index < activeIndex || (isCurrentDone && index === activeIndex)) {
      state = "done";
    } else if (index === activeIndex || (isCurrentDone && index === activeIndex + 1)) {
      state = "active";
    }

    return {
      id: stage.key,
      number: index + 1,
      label: stage.label,
      state,
    };
  });

  if (steps.length > 0 && !steps.some((step) => step.state === "active")) {
    const fallbackIndex = Math.min(Math.max(activeIndex, 0), steps.length - 1);
    steps[fallbackIndex] = {
      ...steps[fallbackIndex],
      state: "active",
    };
  }

  return steps;
};

const buildJourneyFromCase = (
  clientCase: ClientCaseDetail,
): ClientJourneyResponse => {
  const workflowSteps = normalizeCaseStages(clientCase);
  const steps = workflowSteps;
  const safeSteps =
    steps.length > 0
      ? steps
      : [
          {
            id: clientCase.lifecycle_status || "current_stage",
            number: 1,
            label: clientCase.current_stage_label ?? "Current stage",
            state: clientCase.closed_at ? ("done" as const) : ("active" as const),
          },
        ];

  return {
    journey: {
      title: clientCase.workflow_template_name?.trim() || "Case journey",
      steps: safeSteps,
    },
    nextSteps: [],
  };
};

export const useClientDashboard = (): UseClientDashboardResult => {
  const { user } = useAuth();
  const [clientCase, setClientCase] = useState<ClientCaseDetail | null>(null);
  const [journey, setJourney] = useState<ClientJourneyResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setError("Client session is missing.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const caseRes = await clientService.getMyCase();
      setClientCase(caseRes);
      setJourney(buildJourneyFromCase(caseRes));
    } catch (err) {
      setError(toErrorMessage("Could not load client portal data.", err));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    clientCase,
    journey,
    loading,
    error,
    refresh: load,
  };
};
